import { ArrowLeft } from "lucide-react";
import * as React from "react";
import { Link, useParams } from "react-router";

import { useGetSpendFamiliesQuery, useGetSpendSeriesQuery, useGetSpendSettingsQuery } from "@/api/spend";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FamilySummaryDetails } from "./components/family-summary-details";
import { FamilyTabs } from "./components/family-tabs";
import { PeriodControls } from "./components/period-controls";
import { SpendDataTable } from "./components/spend-data-table";
import { SpendHeadline } from "./components/spend-headline";
import { SpendProfile } from "./components/spend-profile";
import { hasSpendProfile } from "./components/spend-profile-html";
import { useSpendQueryState } from "./use-spend-query-state";

const SpendChart = React.lazy(() =>
  import("./components/spend-chart").then(module => ({ default: module.SpendChart })),
);

export const SupplierSpendPage = () => {
  const { supplierId = "" } = useParams<{ supplierId: string }>();
  const { data: settings } = useGetSpendSettingsQuery();
  const state = useSpendQueryState(settings?.default_period_type);
  const { data: familyPage } = useGetSpendFamiliesQuery(
    { supplierId, ownerUserId: state.ownerUserId || undefined },
    { skip: !supplierId, refetchOnFocus: true },
  );
  const { data, isLoading, isFetching, isError, refetch, error } = useGetSpendSeriesQuery(
    {
      supplierId,
      period_type: state.periodType,
      year: state.year,
      family_id: state.familyId,
      owner_user_id: state.ownerUserId || undefined,
      year_window_start: state.view === "year" ? Math.max(1, state.year - 4) : undefined,
    },
    { skip: !supplierId, refetchOnFocus: true, refetchOnMountOrArgChange: true },
  );
  const families = familyPage?.items ?? data?.families ?? [];
  // Only the series response carries the contract-summary text for each family.
  const seriesFamilies = data?.families ?? [];
  const selectedFamily =
    state.familyId === "all" ? undefined : seriesFamilies.find(family => family.relation_id === state.familyId);
  // Spend Profile leads when the contract summary has one; otherwise an empty
  // first tab would hide the spend data that is there.
  const profileAvailable = hasSpendProfile(seriesFamilies);
  const [detailTab, setDetailTab] = React.useState<"profile" | "data" | null>(null);
  const activeDetailTab = detailTab ?? (profileAvailable ? "profile" : "data");
  const availableYears = data?.available_years;
  const { update, year } = state;
  React.useEffect(() => {
    const years = availableYears ?? [];
    if (years.length && !years.includes(year)) {
      update({
        year: years.reduce((closest, value) => (Math.abs(value - year) < Math.abs(closest - year) ? value : closest)),
      });
    }
  }, [availableYears, update, year]);
  const buckets = data
    ? (state.view === "month" ? data.monthly : data.yearly).filter(bucket => bucket.family_id === "all")
    : [];

  return (
    <main
      className="-mx-4 -my-8 min-h-screen bg-[#f7f3ee] px-5 py-7 sm:-mx-6 lg:-mx-8 lg:px-8"
      aria-label="Supplier spend detail"
    >
      <div className="mx-auto max-w-7xl">
        <Link
          to={`/spend-centre?period_type=${state.periodType}&year=${state.year}${state.ownerUserId ? `&owner_user_id=${encodeURIComponent(state.ownerUserId)}` : ""}`}
          className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-[#111b35]"
        >
          <ArrowLeft className="size-4" /> Back to Spend Centre
        </Link>
        <header className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#111b35]">Supplier spend</h1>
            <p className="mt-1 text-sm text-gray-500">Monthly invoices, cumulative actuals and annual comparison.</p>
          </div>
          <PeriodControls
            periodType={state.periodType}
            year={state.year}
            years={data?.available_years ?? []}
            onPeriodChange={period_type => state.update({ period_type })}
            onYearChange={year => state.update({ year })}
          />
        </header>
        <section className="mt-6 overflow-hidden rounded-lg border border-[#ddd5cb] bg-white">
          <FamilyTabs
            families={families}
            selected={state.familyId}
            onSelect={family_id => state.update({ family_id })}
          />
          {selectedFamily ? <FamilySummaryDetails family={selectedFamily} /> : null}
        </section>
        <div className="mt-4 flex justify-end">
          <fieldset className="flex rounded-md border border-[#d8cfc5] bg-white p-1">
            <legend className="sr-only">Chart interval</legend>
            {(["month", "year"] as const).map(view => (
              <button
                key={view}
                type="button"
                aria-pressed={state.view === view}
                onClick={() => state.update({ view })}
                className={`rounded px-3 py-1.5 text-sm font-semibold ${state.view === view ? "bg-[#111b35] text-white" : "text-gray-600"}`}
              >
                {view === "month" ? "Month view" : "Year view"}
              </button>
            ))}
          </fieldset>
        </div>
        {isLoading ? <div className="p-12 text-center text-sm text-gray-500">Loading supplier spend…</div> : null}
        {isError ? (
          <div className="mt-6 rounded-lg border border-red-200 bg-white p-10 text-center">
            <p className="text-sm text-red-700">
              {(error as { data?: { detail?: { message?: string } } })?.data?.detail?.message ??
                "Supplier spend could not be loaded."}
            </p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        ) : null}
        {data ? (
          <div className={isFetching ? "mt-6 space-y-5 opacity-70" : "mt-6 space-y-5"}>
            <SpendHeadline
              estimated={data.estimated}
              estimatedTotal={data.estimated_total}
              actual={data.actual}
              actualQualityFlags={data.actual_quality_flags}
            />
            <section className="rounded-lg border border-[#ddd5cb] bg-white p-4" aria-label="Spend chart">
              <React.Suspense
                fallback={
                  <div className="flex h-[380px] items-center justify-center text-sm text-gray-500">
                    Loading spend chart…
                  </div>
                }
              >
                <SpendChart
                  buckets={state.view === "month" ? data.monthly : data.yearly}
                  families={families}
                  selectedFamilyId={state.familyId}
                  annualEstimateGbp={data.estimated.amount_gbp}
                  showEstimatedPace={state.view === "month"}
                />
              </React.Suspense>
            </section>
            <Tabs
              value={activeDetailTab}
              onValueChange={value => setDetailTab(value as "profile" | "data")}
              className="overflow-hidden rounded-lg border border-[#ddd5cb] bg-white"
            >
              <div className="border-b border-[#e4ddd5] px-5 pt-3">
                <TabsList aria-label="Spend details" className="h-auto gap-1 rounded-none bg-transparent p-0">
                  {(
                    [
                      ["profile", "Spend Profile"],
                      ["data", "Spend data"],
                    ] as const
                  ).map(([value, label]) => (
                    <TabsTrigger
                      key={value}
                      value={value}
                      className="rounded-none border-b-2 border-transparent px-4 py-2.5 font-semibold text-gray-500 data-[state=active]:border-desert-sand-700 data-[state=active]:bg-transparent data-[state=active]:text-[#111b35] data-[state=active]:shadow-none"
                    >
                      {label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
              <TabsContent value="profile" className="m-0">
                <section aria-label="Spend profile">
                  <div className="border-b border-[#e4ddd5] px-5 py-4">
                    <h2 className="font-semibold text-[#111b35]">Spend Profile</h2>
                    <p className="text-xs text-gray-500">
                      Extracted from the contract summary, in the contract currency.
                    </p>
                  </div>
                  <SpendProfile families={seriesFamilies} />
                </section>
              </TabsContent>
              <TabsContent value="data" className="m-0">
                <section aria-label="Spend data table">
                  <div className="border-b border-[#e4ddd5] px-5 py-4">
                    <h2 className="font-semibold text-[#111b35]">Spend data</h2>
                    <p className="text-xs text-gray-500">
                      Exact backend values; unavailable and incomplete periods remain explicit.
                    </p>
                  </div>
                  <SpendDataTable buckets={buckets} />
                </section>
              </TabsContent>
            </Tabs>
          </div>
        ) : null}
      </div>
    </main>
  );
};
